import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import HaloRing from "@/components/HaloRing";
import HaloWordmark from "@/components/HaloWordmark";

export default function LoginPage() {
  const { login, signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const defaultTab = params.get("mode") === "signup" ? "signup" : "login";
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [remember, setRemember] = useState(true);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, password, remember); nav("/"); toast.success("Welcome back"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Invalid credentials"); }
    finally { setBusy(false); }
  };
  const doSignup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await signup(email, password, name); nav("/"); toast.success("Welcome to Mahano OS"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not sign up"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between py-14 px-12 lg:px-16 bg-[hsl(var(--mahano-ink))] text-[hsl(var(--mahano-ivory))]">
        <div className="studio-glow pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="relative flex items-center gap-3">
          <HaloRing size={34} tone="dark" />
          <HaloWordmark height={26} tone="dark" data-testid="login-wordmark" />
          <Link to="/welcome" data-testid="login-back-to-site"
                className="ml-auto text-xs text-white/55 hover:text-white">
            ← Back to site
          </Link>
        </div>
        <div className="relative max-w-md studio-rise">
          <h1 className="text-5xl font-semibold tracking-tight leading-[1.02]">
            Life and work, in harmony.
          </h1>
          <p className="mt-5 text-white/60 leading-relaxed text-[15px]">
            Mahano OS holds your contacts, projects, finance, scheduling and campaigns in one
            calm place — so the studio runs itself while you do the work that matters.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-3 text-[11px] text-white/70">
            {["CRM", "Projects", "Finance", "Calendar", "Campaigns", "Analytics"].map((k) => (
              <div key={k} className="rounded-full border border-white/12 bg-white/[0.05] px-3 py-2 text-center">{k}</div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-white/45 flex items-center gap-1.5">
          <HaloWordmark height={13} tone="dark" className="opacity-80" /> · Life and work, in harmony
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md studio-card p-7 sm:p-9 studio-rise studio-rise-2">
          <Tabs defaultValue={defaultTab}>
            <TabsList data-testid="auth-tabs" className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger data-testid="tab-login" value="login">Sign in</TabsTrigger>
              <TabsTrigger data-testid="tab-signup" value="signup">Create account</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={doLogin} className="space-y-4" data-testid="login-form">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                    <Checkbox data-testid="login-remember" checked={remember}
                              onCheckedChange={(v) => setRemember(Boolean(v))} />
                    Keep me signed in
                  </label>
                  <Link to="/forgot-password" data-testid="login-forgot-link"
                        className="text-xs text-[hsl(var(--studio-primary))] hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full h-11 bg-[hsl(var(--studio-primary))] hover:bg-[hsl(var(--studio-primary-hover))]">
                  {busy ? "Signing in…" : "Sign in"}
                </Button>
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  {remember
                    ? "You'll stay signed in on this device for 60 days."
                    : "You'll be signed out when you close the browser."}
                </p>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={doSignup} className="space-y-4" data-testid="signup-form">
                <div>
                  <Label htmlFor="s-name">Your name</Label>
                  <Input id="s-name" data-testid="signup-name" required value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="s-email">Email</Label>
                  <Input id="s-email" data-testid="signup-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="s-pass">Password (min 8 chars)</Label>
                  <Input id="s-pass" data-testid="signup-password" type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                <Button data-testid="signup-submit" type="submit" disabled={busy} className="w-full h-11 bg-[hsl(var(--studio-primary))] hover:bg-[hsl(var(--studio-primary-hover))]">
                  {busy ? "Creating account…" : "Create account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            By continuing you accept the Mahano OS terms. Everything is configurable in Settings.
          </p>
        </div>
      </div>
    </div>
  );
}
